import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  getDailyPageErrorMessage,
  getDailyPagePayload,
  getDailyPageSchedule,
  isDailyPageApiSuccess,
} from '../../services/dailyPageApi';
import {
  addDays,
  formatIstDate,
  hiddenSectionKeys,
  istToday,
  monthBounds,
  normalizeSectionsVisible,
  parseScheduleDays,
  statusColor,
} from './dailyTasksUtils';

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function sectionChip(label, ok, detail) {
  return (
    <Chip
      size="small"
      label={detail ? `${label}: ${detail}` : label}
      color={ok ? 'success' : 'default'}
      variant={ok ? 'filled' : 'outlined'}
      sx={{ height: 20, fontSize: 10 }}
    />
  );
}

function DailyTasksCalendarPage() {
  const navigate = useNavigate();
  const fallbackToday = istToday();
  const [cursor, setCursor] = useState(() => {
    const [y, m] = fallbackToday.split('-').map(Number);
    return { year: y, month: m - 1 };
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [daysByDate, setDaysByDate] = useState({});
  const [today, setToday] = useState(fallbackToday);

  const { start, end, lastDay } = useMemo(
    () => monthBounds(cursor.year, cursor.month),
    [cursor.year, cursor.month]
  );

  const loadMonth = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getDailyPageSchedule({
        startDate: start,
        endDate: end,
        limit: 100,
        offset: 0,
      });
      if (!isDailyPageApiSuccess(res)) {
        setError(getDailyPageErrorMessage(res, 'Failed to load schedule.'));
        setDaysByDate({});
        return;
      }
      const parsed = parseScheduleDays(getDailyPagePayload(res));
      setToday(parsed.today);
      const map = {};
      parsed.days.forEach((day) => {
        if (day?.activity_date) map[day.activity_date] = day;
      });
      setDaysByDate(map);
    } catch (err) {
      setError(err.message || 'Failed to load schedule.');
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const cells = useMemo(() => {
    const first = new Date(Date.UTC(cursor.year, cursor.month, 1));
    const jsDay = first.getUTCDay();
    const mondayOffset = (jsDay + 6) % 7;
    const total = mondayOffset + lastDay;
    const rows = Math.ceil(total / 7);
    const grid = [];
    for (let i = 0; i < rows * 7; i += 1) {
      const dayNum = i - mondayOffset + 1;
      if (dayNum < 1 || dayNum > lastDay) {
        grid.push(null);
      } else {
        grid.push(
          `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
        );
      }
    }
    return grid;
  }, [cursor.year, cursor.month, lastDay]);

  const monthLabel = new Date(Date.UTC(cursor.year, cursor.month, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={() =>
              setCursor((c) => {
                const m = c.month - 1;
                return m < 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: m };
              })
            }
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center' }}>
            {monthLabel}
          </Typography>
          <IconButton
            onClick={() =>
              setCursor((c) => {
                const m = c.month + 1;
                return m > 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: m };
              })
            }
          >
            <ChevronRightIcon />
          </IconButton>
          <Chip size="small" label="IST · Asia/Kolkata" variant="outlined" />
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadMonth} disabled={loading}>
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            component={Link}
            to={`/admin/daily-tasks/day/${addDays(today, 1)}`}
          >
            Open tomorrow
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 1,
              mb: 1,
            }}
          >
            {WEEKDAY_HEADERS.map((d) => (
              <Typography key={d} variant="caption" color="text.secondary" fontWeight={600}>
                {d}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 1,
            }}
          >
            {cells.map((date, idx) => {
              if (!date) {
                return (
                  <Paper
                    key={`empty-${idx}`}
                    variant="outlined"
                    sx={{ p: 1, minHeight: 130, bgcolor: '#fafafa' }}
                  />
                );
              }
              const day = daysByDate[date];
              const sections = day?.sections || {};
              const status = day?.status || 'scheduled';
              const isToday = date === today;
              const missions = sections.missions || {};
              const wheel = sections.fe_wheel || {};
              const challenge = sections.daily_challenge || {};
              const opinion = sections.opinion || {};
              const fotd = sections.fan_of_the_day || {};
              const visibility = normalizeSectionsVisible(day || {});
              const hidden = hiddenSectionKeys(visibility);

              return (
                <Paper
                  key={date}
                  variant="outlined"
                  sx={{
                    p: 1,
                    minHeight: 130,
                    borderColor: isToday ? 'primary.main' : 'divider',
                    borderWidth: isToday ? 2 : 1,
                    opacity: hidden.length >= 5 ? 0.85 : 1,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 },
                  }}
                  onClick={() => navigate(`/admin/daily-tasks/day/${date}`)}
                  title={formatIstDate(date)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {Number(date.slice(-2))}
                    </Typography>
                    <Chip
                      size="small"
                      label={status}
                      color={statusColor(status)}
                      sx={{ height: 20, fontSize: 10 }}
                    />
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {sectionChip(
                      'Missions',
                      !!missions.has_mission && visibility.missions !== false,
                      visibility.missions === false
                        ? 'hidden'
                        : missions.has_mission
                          ? `${missions.task_count || 0} tasks`
                          : 'missing'
                    )}
                    {sectionChip(
                      'Wheel',
                      !!wheel.assigned && visibility.fe_wheel !== false,
                      visibility.fe_wheel === false
                        ? 'hidden'
                        : wheel.assigned
                          ? wheel.title || 'assigned'
                          : 'none'
                    )}
                    {sectionChip(
                      'Challenge',
                      (challenge.count || 0) > 0 && visibility.daily_challenge !== false,
                      visibility.daily_challenge === false ? 'hidden' : challenge.count || 0
                    )}
                    {sectionChip(
                      'Opinion',
                      (opinion.count || 0) > 0 && visibility.opinion !== false,
                      visibility.opinion === false ? 'hidden' : opinion.count || 0
                    )}
                    {fotd.availability && (
                      <Chip
                        size="small"
                        label={
                          visibility.fan_of_the_day === false
                            ? 'FOTD hidden'
                            : `FOTD ${fotd.availability === 'assigned' ? '✓' : '…'}`
                        }
                        color={visibility.fan_of_the_day === false ? 'warning' : 'default'}
                        sx={{ height: 20, fontSize: 10 }}
                        variant="outlined"
                      />
                    )}
                    {hidden.length > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        label={`${hidden.length} off`}
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}

export default DailyTasksCalendarPage;
